/**
 * Mock Gmail Client
 * Implements IGmailClient interface against local mock storage.
 */

import { ApiEnvelope, GmailMessage, GmailThread } from '../../domain/types';
import { INITIAL_GMAIL_THREADS } from '../../data/initialData';
import { IGmailClient, ClientConfig, ProviderConnectionProbe } from './types';
import {
  simulateNetwork,
  DEFAULT_CONFIG,
  isMockSourceRejected,
  mockRejectedCredentialMessage,
} from './mockClientUtils';

export class MockGmailClient implements IGmailClient {
  private threads: GmailThread[];
  private config: ClientConfig;
  private threadSummaries = new Map<string, { summary: string; actionItem?: string }>();

  constructor(config: ClientConfig = DEFAULT_CONFIG, includeFixtures = true) {
    this.config = config;
    // Deep clone so mutations never bleed back into the shared fixture array.
    this.threads = includeFixtures
      ? INITIAL_GMAIL_THREADS.map((t) => ({
          ...t,
          messages: t.messages.map((m) => ({ ...m, bodyParagraphs: [...m.bodyParagraphs] })),
        }))
      : [];
  }

  async getMessages(): Promise<ApiEnvelope<GmailThread[]>> {
    return simulateNetwork(() => [...this.threads], this.config);
  }

  async getMessage(id: string): Promise<ApiEnvelope<GmailThread | null>> {
    return simulateNetwork(() => {
      const match = this.threads.find((t) => t.id === id || t.messages.some((m) => m.id === id));
      return match ? { ...match, messages: [...match.messages] } : null;
    }, this.config);
  }

  private findMessageLocation(id: string): { thread: GmailThread; message: GmailMessage } | null {
    for (const thread of this.threads) {
      const message = thread.messages.find((m) => m.id === id);
      if (message) return { thread, message };
    }
    return null;
  }

  async markRead(id: string): Promise<ApiEnvelope<GmailMessage>> {
    return simulateNetwork(() => {
      const loc = this.findMessageLocation(id);
      if (!loc) throw new Error(`Gmail message ${id} not found`);
      loc.message.unread = false;
      loc.thread.unread = loc.thread.messages.some((m) => m.unread);
      return { ...loc.message };
    }, this.config);
  }

  async replyToMessage(id: string, text: string): Promise<ApiEnvelope<GmailMessage>> {
    return simulateNetwork(() => {
      const loc = this.findMessageLocation(id);
      if (!loc) throw new Error(`Gmail message ${id} not found`);
      const trimmed = text.trim();
      if (!trimmed) throw new Error('Reply text cannot be empty');
      loc.message.unread = false;

      const replyMsg: GmailMessage = {
        id: `ML-reply-${Date.now()}`,
        threadId: loc.thread.id,
        from: {
          name: 'Juan Chez',
          email: 'ti@genlogs.io',
          initials: 'JC',
          color: 'var(--z-accent)',
        },
        to: loc.message.from.email,
        subject: loc.thread.subject.startsWith('Re: ')
          ? loc.thread.subject
          : `Re: ${loc.thread.subject}`,
        bodyParagraphs: [trimmed],
        kind: 'Interno',
        unread: false,
        blocksWork: false,
        receivedAt: new Date().toISOString(),
      };

      loc.thread.messages = [...loc.thread.messages, replyMsg];
      loc.thread.unread = loc.thread.messages.some((m) => m.unread);
      loc.thread.receivedAt = replyMsg.receivedAt;

      return { ...replyMsg };
    }, this.config);
  }

  async generateThreadSummary(
    id: string,
    options?: { force?: boolean },
  ): Promise<ApiEnvelope<{ summary: string; actionItem?: string; refreshed?: boolean }>> {
    return simulateNetwork(() => {
      const thread = this.threads.find((t) => t.id === id || t.messages.some((m) => m.id === id));
      if (!thread) {
        throw new Error(`Gmail thread ${id} not found`);
      }
      const force = options?.force === true;
      const cached = this.threadSummaries.get(thread.id);
      if (cached && !force) {
        return { ...cached, refreshed: false };
      }
      const firstMessage = thread.messages[0];
      const generated = {
        summary: `${thread.subject}: ${firstMessage?.bodyParagraphs?.[0] ?? 'sin contenido adicional'}.`,
        actionItem: thread.blocksWork
          ? 'Responder antes de continuar con el trabajo bloqueado.'
          : undefined,
      };
      this.threadSummaries.set(thread.id, generated);
      return { ...generated, refreshed: force };
    }, this.config);
  }

  async generateReplyDraft(
    id: string,
    hint?: string,
  ): Promise<ApiEnvelope<{ draft: string; provider?: string; model?: string }>> {
    return simulateNetwork(() => {
      const subject = this.resolveSubject(id);
      if (!subject) throw new Error(`Gmail message ${id} not found`);
      const idea = hint?.trim();
      const draft = idea
        ? `${idea}\n\nSaludos,\nTú`
        : `Gracias por tu correo sobre "${subject}". Lo reviso y te confirmo en breve.\n\nSaludos,\nTú`;
      return { draft, provider: 'deterministic' };
    }, this.config);
  }

  private resolveSubject(id: string): string | null {
    const byMessage = this.findMessageLocation(id);
    if (byMessage) return byMessage.thread.subject;
    const thread = this.threads.find((t) => t.id === id);
    return thread ? thread.subject : null;
  }

  async testConnection(): Promise<ApiEnvelope<ProviderConnectionProbe>> {
    if (isMockSourceRejected('gmail')) {
      return simulateNetwork(
        () => ({
          provider: 'gmail',
          status: 'error' as const,
          message: mockRejectedCredentialMessage('gmail'),
          checkedAt: new Date().toISOString(),
        }),
        this.config,
      );
    }
    return simulateNetwork(
      () => ({
        provider: 'gmail',
        status: 'ok' as const,
        message: 'Conectado (mock).',
        checkedAt: new Date().toISOString(),
      }),
      this.config,
    );
  }
}
